<script lang="ts">
import { computed, provide } from 'vue';

import '@sg/src/View/Components/Atoms/Button/button.css';
import '@sg/src/View/Components/Atoms/Text/text.css';
import { PhxsgIcon, PhxsgIconIds } from '@sg/src/View/Components/Atoms/Icon';
import type { PhxsgIconProps } from '@sg/src/View/Components/Atoms/Icon';
import { PhxsgLoaderProps } from '@sg/src/View/Components/Atoms/Loader';
import { PhxsgButton as PhxsgCoreButton, PhxsgButtonProps } from '@sg-core/src/View/Components/Atoms/Button';

export type PiaButtonColor = 'primary' | 'secondary';
export type PiaButtonVariant = 'text' | 'specs' | 'link' | 'ghost' | 'plain';

export type PiaPhxsgButtonProps = Omit<PhxsgButtonProps, 'variant' | 'iconId'> & {
  color?: PiaButtonColor;
  variant?: PiaButtonVariant;
  iconId?: PhxsgIconIds;
};
</script>
<script setup lang="ts">
const {
  color = 'primary',
  variant = 'text',
  iconId,
  icon,
  iconOnly,
  loaderProps,
  ...buttonProps
} = defineProps<PiaPhxsgButtonProps>();

const btnIcon = computed(() => iconId && { id: iconId });

const iconOnlyIcon = computed<PhxsgIconProps | undefined>(() => {
  if (!iconOnly) {
    return undefined;
  }

  if (btnIcon.value) {
    return btnIcon.value;
  }

  if (icon && 'start' in icon && icon.start) {
    return icon.start as PhxsgIconProps;
  }

  if (icon && 'end' in icon && icon.end) {
    return icon.end as PhxsgIconProps;
  }

  if (icon && 'id' in icon) {
    return icon as PhxsgIconProps;
  }

  return undefined;
});

const computedIcon = computed(() => {
  if (iconOnly) {
    return undefined;
  }

  return icon || (btnIcon.value ? { start: btnIcon.value } : undefined);
});

const computedLoaderProps = computed((): PhxsgLoaderProps => ({
  ...(loaderProps ?? {}),
  type: 'circle',
}));

provide('PhxsgIcon', PhxsgIcon);
</script>

<template>
  <PhxsgCoreButton
    v-bind="{ ...buttonProps, variant }"
    :class="{ [`phxsg-btn--${color}`]: variant === 'plain' }"
    :icon-only="iconOnly"
    :icon="computedIcon"
    :loader-props="computedLoaderProps"
  >
    <template v-if="$slots.iconStart" #start>
      <slot name="iconStart" />
    </template>

    <slot />

    <template v-if="$slots.content || iconOnly" #content>
      <slot name="content">
        <span v-if="iconOnly && iconOnlyIcon" class="phxsg-btn__icon">
          <PhxsgIcon v-bind="iconOnlyIcon" />
        </span>
      </slot>
    </template>

    <template v-if="$slots.iconEnd" #end>
      <slot name="iconEnd" />
    </template>
  </PhxsgCoreButton>
</template>
